import { isPlatformBrowser } from '@angular/common';
import {
    Directive,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    PLATFORM_ID,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    takeUntil,
} from 'rxjs/operators';

@Directive({
    selector: '[appIntersectionObserver]',
    standalone: true,
})
export class IntersectionObserverDirective implements OnInit, OnDestroy {
    @Input() intersectionDebounce = 50;
    @Input() intersectionRootMargin = '0px';
    @Input() intersectionRoot: HTMLElement;
    @Input() intersectionThreshold: number;

    @Output() visibilityChange = new EventEmitter<Boolean>();

    private destroy$ = new Subject();

    constructor(
        private element: ElementRef,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {}

    ngOnInit() {
        if (isPlatformBrowser(this.platformId)) {
            const element = this.element.nativeElement;

            const config = {
                root: this.intersectionRoot,
                rootMargin: this.intersectionRootMargin,
                threshold: +this.intersectionThreshold,
            };

            this.fromIntersectionObserver(element, config)
                .pipe(
                    debounceTime(this.intersectionDebounce),
                    map(
                        entry =>
                            entry.isIntersecting &&
                            entry.intersectionRatio >=
                                this.intersectionThreshold
                    ),
                    distinctUntilChanged(),
                    takeUntil(this.destroy$)
                )
                .subscribe(status => {
                    this.visibilityChange.emit(status);
                });
        }
    }

    fromIntersectionObserver(
        element: HTMLElement,
        config: IntersectionObserverInit
    ) {
        return new Observable<IntersectionObserverEntry>(subscriber => {
            const intersectionObserver = new IntersectionObserver(
                (entries: IntersectionObserverEntry[] = []) => {
                    subscriber.next(entries[0] || null);
                },
                config
            );
            intersectionObserver.observe(element);
            return () => {
                intersectionObserver.disconnect();
            };
        });
    }

    ngOnDestroy() {
        this.destroy$.next(null);
    }
}
